import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";

function getRect(el) {
  if (!el) return null;
  const r = el.getBoundingClientRect();
  return { top: r.top, left: r.left, width: r.width, height: r.height };
}

export default function TourGuide({
  steps = [],
  onClose,
  startIndex = 0,
  overlayZ = 1000,
}) {
  const [index, setIndex] = useState(startIndex);
  const [rect, setRect] = useState(null);

  const currentStep = steps[index];

  const findTarget = useCallback(() => {
    if (!currentStep) return;
    const el = document.querySelector(currentStep.selector);
    setRect(getRect(el));
  }, [currentStep]);

  useLayoutEffect(() => {
    findTarget();
  }, [index, findTarget]);

  useEffect(() => {
    const handleResize = () => findTarget();
    window.addEventListener("resize", handleResize);
    window.addEventListener("scroll", handleResize, true);
    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("scroll", handleResize, true);
    };
  }, [findTarget]);

  const next = () => {
    if (index < steps.length - 1) setIndex(index + 1);
    else onClose && onClose();
  };

  const skip = () => onClose && onClose();

  const tooltipPos = useMemo(() => {
    const tooltipWidth = 260;
    const margin = 12;

    if (!rect) {
      // Center tooltip on screen when target is not found
      const top = window.innerHeight / 2 - 100;
      const left = window.innerWidth / 2 - tooltipWidth / 2;
      return { top, left };
    }

    const offset = 200; // raise tooltip further above the target
    // Always position ABOVE the target element
    const top = Math.max(rect.top - offset, margin);
    // Center horizontally over the target, with clamping
    const centeredLeft = rect.left + rect.width / 2 - tooltipWidth / 2;
    const left = Math.min(
      Math.max(centeredLeft, margin),
      window.innerWidth - tooltipWidth - margin
    );
    return { top, left };
  }, [rect]);

  if (!currentStep) return null;

  return (
    <div className="tour-overlay" style={{ zIndex: overlayZ }}>
      {rect && (
        <>
          {/* Overlay parts around the target to keep it undimmed */}
          <div
            className="tour-dim-part"
            onClick={skip}
            style={{
              top: 0,
              left: 0,
              width: "100vw",
              height: Math.max(rect.top - 8, 0),
            }}
          />
          <div
            className="tour-dim-part"
            onClick={skip}
            style={{
              top: rect.top - 8,
              left: 0,
              width: Math.max(rect.left - 8, 0),
              height: rect.height + 16,
            }}
          />
          <div
            className="tour-dim-part"
            onClick={skip}
            style={{
              top: rect.top - 8,
              left: rect.left + rect.width + 8,
              width: Math.max(
                window.innerWidth - (rect.left + rect.width + 8),
                0
              ),
              height: rect.height + 16,
            }}
          />
          <div
            className="tour-dim-part"
            onClick={skip}
            style={{
              top: rect.top + rect.height + 8,
              left: 0,
              width: "100vw",
              height: Math.max(
                window.innerHeight - (rect.top + rect.height + 8),
                0
              ),
            }}
          />
        </>
      )}

      {rect && (
        <div
          className="tour-highlight"
          style={{
            top: rect.top - 8,
            left: rect.left - 8,
            width: rect.width + 16,
            height: rect.height + 16,
          }}
        />
      )}

      <div
        className="tour-tooltip"
        style={{ top: tooltipPos.top, left: tooltipPos.left }}
      >
        <div className="tour-title-row">
          <h4>{currentStep.title}</h4>
          <button className="tour-close" onClick={skip} aria-label="Skip">
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>
        <p className="tour-desc">{currentStep.description}</p>
        <div className="tour-actions">
          <div style={{ flex: 1 }} />
          <button className="tour-btn secondary" onClick={skip}>
            Skip
          </button>
          <button className="tour-btn primary" onClick={next}>
            {index < steps.length - 1 ? "Next" : "Finish"}
          </button>
        </div>
      </div>
    </div>
  );
}
